import { computed, ref } from 'vue';
import useMainApiRequest from '@composables/useMainApiRequest';
import useForeignApiRequest from '@composables/useForeignApiRequest';
import { registerCacheInvalidation } from '@composables/useCache';
import useModels from '@composables/useModels';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { has, set, get, unset } from 'lodash-es';
import { isCachingStatus, isCalibratedStatus } from '@composables/useStatus';
import useToast from '@composables/useToast';
import type { CsvCell } from '@composables/useExport';

export type TmMatrix = {
  id: number;
  name: string;
  description: string;
  createdAt: TmDate;
  isActive: boolean;
  isDefault: boolean;
  owner: string;
  matrixRefId: number | null;
  status: string;
};

const cachedMatrices = ref<Record<TmMatrix['id'], TmMatrix>>({});
const cachedCsv = ref<Record<number, CsvCell[]>>({});
const isOverviewCached = ref(false);

export default function useModelMatrices() {
  const router = useRouter();
  const store = useStore();
  const { t } = useI18n();
  const { makeRequest } = useMainApiRequest();
  const { makeRequest: makeModelApiRequest } = useForeignApiRequest();
  const { fetchModels, refreshModel, activeModel } = useModels();
  const { addToast } = useToast();

  const trafficModel = computed<TmTrafficModel>(() => store.state.scenarios.model);
  const matrices = computed(() => Object.values(cachedMatrices.value));

  const fetchMatrices = () => {
    if (isCacheAvailable() && !isMatrixRefreshing()) return;

    return makeRequest({
      url: `matrices/traffic-model/${trafficModel.value.id}`,
      method: 'get',
      message: {
        error: {
          404: { summary: t('models.no matrices found'), severity: 'info' },
          default: t('models.error while fetching matrices'),
        },
      },
      onSuccess: ({ matrices }: { matrices: TmMatrix[] }) => {
        matrices.forEach((matrix) => set(cachedMatrices.value, matrix.id, matrix));
        isOverviewCached.value = true;
      },
    });
  };

  const fetchMatrix = (matrixId: TmMatrix['id']) => {
    if (isCacheAvailable(matrixId) && !isMatrixRefreshing(matrixId)) return;

    return makeRequest({
      url: `matrices/${matrixId}`,
      method: 'get',
      message: {
        error: t('models.error while fetching matrix'),
      },
      onSuccess: ({ matrix }: { matrix: TmMatrix }) => set(cachedMatrices.value, matrix.id, matrix),
    });
  };

  const getMatrixCsv = async (matrixRefId: number): Promise<CsvCell[]> => {
    if (cachedCsv.value[matrixRefId]) return cachedCsv.value[matrixRefId];

    await makeModelApiRequest({
      url: `${trafficModel.value.endPoint}/pairs/${trafficModel.value.name}/${matrixRefId}`,
      params: { format: 'CSV' },
      timeout: 30 * 1000, // 30 seconds
      method: 'get',
      message: { error: t('models.error while fetching matrix CSV') },
      onSuccess: (csvString: string) => {
        cachedCsv.value[matrixRefId] = _parseCsvString(csvString);
      },
    });

    return cachedCsv.value[matrixRefId] || [];
  };

  const createMatrix = (matrix: Partial<TmMatrix>) => {
    return makeRequest({
      url: `matrices`,
      method: 'post',
      data: {
        name: matrix.name,
        description: matrix.description,
        matrixRefId: matrix.matrixRefId,
        trafficModelId: trafficModel.value.id,
      },
      message: {
        error: t('models.error while creating matrix'),
        success: t('models.matrix created successfully'),
      },
      onSuccess: (result: { matrix: TmMatrix }) => {
        set(cachedMatrices.value, result.matrix.id, result.matrix);
        router.push({ name: 'models.matrices' });
      },
    });
  };

  const updateMatrix = (
    matrixId: TmMatrix['id'],
    matrix: Partial<TmMatrix>,
    activateMatrix = false,
    setDefault = false,
  ) => {
    return makeRequest({
      url: `matrices/${matrixId}`,
      method: 'patch',
      data: {
        name: matrix.name,
        description: matrix.description,
        // NOTE: only activation is possible (server validator refuses anything but true value)
        //       deactivation is automatic on the server when another matrix is activated
        //       also we should not send the 'isActive' prop in any other case, because server would than check activness of sent matrix.
        ...(activateMatrix && matrix.isActive ? { isActive: true } : {}),
        ...(setDefault && matrix.isDefault ? { isDefault: true } : {}),
      },
      message: {
        success: t('models.matrix updated successfully'),
        error: t('models.error while updating matrix'),
      },
      onSuccess: ({ matrix }: { matrix: TmMatrix }) => {
        set(cachedMatrices.value, matrix.id, matrix); // utilized if isActive or isDefault is not changed, in other cases matrix cache are reset
        if (setDefault) invalidateMatrices(); // display changes made to prev default matrix
        if (activateMatrix) refreshModel(trafficModel.value.id); // trigger model refresh to re-compute everything with the newly activated active matrix
        router.push({ name: 'models.matrices' });
      },
    });
  };

  /**
   * Calculate default traffic cache (base model) without scenario re-calculation.
   * For scenario re-calculation use the refreshModel function
   */
  const refreshMatrix = (id: TmMatrix['id']) => {
    return makeRequest({
      url: `matrices/refresh`,
      method: 'post',
      data: { id },
      message: {
        success: {
          severity: 'info',
          summary: t('models.matrix refreshing'),
        },
        error: t('models.error while refreshing matrix'),
      },
      onSuccess: ({ matrix }: { matrix: TmMatrix }) => set(cachedMatrices.value, matrix.id, matrix),
      onFailure: ({ data }) =>
        data?.matrix ? set(cachedMatrices.value, data.matrix.id, data.matrix) : invalidateMatrices(),
    });
  };

  const handleMatrixEnter = async () => {
    await fetchModels(); // ensure traffic models are already fetched
    store.dispatch('map/activateItem', {}); // deactivate any active scenario, si its mods are not displayed on map
  };

  const handleMatrixExit = () => {
    if (['models.matrices.edit', 'models.matrices'].includes(String(router.currentRoute.value.name))) return;
    // un-preview matrix to display original base model on the map
    store.dispatch('map/previewMatrix');
  };

  const getMatrixById = (id: TmMatrix['id']) => get(cachedMatrices.value, id);

  const isMatrixRefreshing = (matrixId?: TmMatrix['id']) => {
    if (!matrixId) return matrices.value.some((mx) => isCachingStatus(mx.status));
    const matrix = getMatrixById(matrixId);
    return isCachingStatus(matrix.status);
  };

  const previewMatrix = (id?: TmMatrix['id']) => {
    if (!id) return store.dispatch('map/previewMatrix');

    const matrix = getMatrixById(id);
    if (isCalibratedStatus(matrix?.status))
      addToast({
        severity: 'info',
        summary: t('models.matrix refresh needed'), // task 86c199heb
      });
    store.dispatch('map/previewMatrix', id);
  };

  return {
    matrices,
    previewedMatrixId: computed<TmMatrix['id'] | undefined>(() => store.getters['map/getPreviewedMatrixId']),
    activeMatrixId: computed(() => activeModel.value.activeMatrixId || undefined),
    fetchMatrices,
    fetchMatrix,
    createMatrix,
    updateMatrix,
    handleMatrixEnter,
    handleMatrixExit,
    refreshMatrix,
    getMatrixById,
    previewMatrix,
    getMatrixCsv,
    invalidateMatrices,
  };
}

const isCacheAvailable = (id?: TmMatrix['id']) => {
  if (id) return has(cachedMatrices.value, id);
  return isOverviewCached.value && !!Object.keys(cachedMatrices.value).length;
};

export const invalidateMatrices = (id?: TmMatrix['id']) => {
  if (id) return unset(cachedMatrices.value, id);
  cachedMatrices.value = {};
  cachedCsv.value = {};
  isOverviewCached.value = false;
};

export const updateMatrixCache = (id: TmMatrix['id'], value: TmMatrix) => set(cachedMatrices.value, id, value);

registerCacheInvalidation('matrices', invalidateMatrices);

const _parseCsvString = (csvString: string): CsvCell[] => {
  const rows = csvString.trim().split(/\r?\n/);
  if (!rows.length) return [];
  const header = rows.shift()?.split(',');
  if (!header) return [];
  const csvCells: CsvCell[] = [];
  for (const row of rows) {
    const columns = row.split(',');
    const cell: CsvCell = {};
    for (let i = 0; i < header.length; i++) {
      cell[header[i].trim()] = columns[i].trim();
    }
    csvCells.push(cell);
  }
  return csvCells;
};
